<!-- routes/ComingSoon/PreregisterForm.svelte -->
<script>
  import Container from "lib/components/Container.svelte";
  import Button from "lib/components/Button.svelte";
  import { trackEvent } from "utils/analytics.js";
  import { onSubmitContactRequest } from "lib/modules/Contact.svelte";
  import { _ } from "svelte-i18n";

  // Form state
  let email = "";
  let isSubmitting = false;
  let emailSubmitted = false;
  let formErrors = {};
  let showErrors = false;

  function resetForm() {
    email = "";
    formErrors = {};
    showErrors = false;
    isSubmitting = false;
  }

  function validateForm() {
    const errors = {};

    if (!email.trim()) {
      errors.email = $_("form.error.required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = $_("form.error.invalid");
    }

    return errors;
  }

  async function handleFormSubmit(event) {
    if (event) {
      event.preventDefault();
    }

    if (isSubmitting) return;

    // Validate form
    formErrors = validateForm();
    showErrors = true;

    if (Object.keys(formErrors).length > 0) {
      const fieldElement = document.getElementById("email");
      if (fieldElement) {
        fieldElement.focus();
      }
      return;
    }

    // Trigger reCAPTCHA verification
    const submitButton = document.querySelector(".email-submit-button button");
    if (submitButton) {
      submitButton.click();
    }
  }

  async function handleSubmit(token) {
    if (isSubmitting) return;

    isSubmitting = true;

    try {
      const success = await onSubmitContactRequest({
        email,
        fullName: "Valued Customer",
        message: "CNY_campaign_preregistration",
        source: "liquidnoble.com",
        token,
      });

      if (success) {
        emailSubmitted = true;
        resetForm();

        if (window.dataLayer) {
          window.dataLayer.push({
            event: "conversion",
            conversion_type: "lead_generation",
            form_type: "preregistration",
            campaign: "cny_gold_remittance",
          });
        }

        trackEvent("Contact", {
          action: "cny_preregistration",
          source: "gold_remittance_landing",
        });
      }
    } catch (error) {
      console.error("Email submission failed:", error);
    } finally {
      isSubmitting = false;
    }
  }

  function handleInputChange() {
    if (showErrors && formErrors.email) {
      formErrors = { ...formErrors };
      delete formErrors.email;
    }
  }
</script>

<!-- Email Collection Section -->
<Container variant="dark" class="email-section">
  <div class="email-content">
    {#if emailSubmitted}
      <div class="success-message" role="alert" aria-live="polite">
        <div class="success-icon">✅</div>
        <h3>{$_("form.success.title")}</h3>
        <p>
          {$_("form.success.description")}
        </p>
        <p class="success-note">
          {$_("form.success.note")}
        </p>
        <Button
          variant="secondary"
          size="medium"
          on:click={() => {
            emailSubmitted = false;
            resetForm();
            if (typeof window !== "undefined") {
              window.location.reload();
            }
          }}
        >
          {$_("form.success.signUpAnother")}
        </Button>
      </div>
    {:else}
      <div class="email-form-container">
        <h2 class="email-title">{$_("form.title")}</h2>
        <p class="email-subtitle">
          {$_("form.subtitle")}
        </p>

        <form on:submit={handleFormSubmit} class="email-form" novalidate>
          <div class="form-group">
            <input
              id="email"
              type="email"
              bind:value={email}
              on:input={handleInputChange}
              placeholder={$_("form.placeholder")}
              class:error={showErrors && formErrors.email}
              aria-describedby={showErrors && formErrors.email
                ? "email-error"
                : null}
              disabled={isSubmitting}
              required
            />
            {#if showErrors && formErrors.email}
              <span id="email-error" class="error-message" role="alert">
                {formErrors.email}
              </span>
            {/if}
          </div>

          <Button
            variant="primary"
            size="large"
            type="submit"
            reCAPTCHA={true}
            dataCallback={handleSubmit}
            disabled={isSubmitting}
            className="email-submit-button"
          >
            {isSubmitting ? $_("form.submitting") : $_("form.submit")}
          </Button>
        </form>

        <p class="email-disclaimer">
          {$_("form.disclaimer")}
        </p>
      </div>
    {/if}
  </div>
</Container>

<style lang="scss">
  /* Email Section */
  .email-section {
    padding: 4rem 0;
  }

  .email-content {
    max-width: 600px;
    margin: 0 auto;
    text-align: center;
  }

  .email-form-container {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(15px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
    padding: 3rem;
  }

  .email-title {
    font-size: 2.2rem;
    font-weight: 700;
    color: var(--text-color1);
    margin-bottom: 1rem;
  }

  .email-subtitle {
    font-size: 1.1rem;
    line-height: 1.6;
    color: var(--text-color2);
    margin-bottom: 2rem;
  }

  .email-form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .form-group input {
    width: 100%;
    padding: 1rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.1);
    color: var(--text-color2);
    font-size: 1rem;
    text-align: center;
    transition: all 0.3s ease;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: var(--text-color1);
      background: rgba(255, 255, 255, 0.15);
      box-shadow: 0 0 0 2px rgba(210, 165, 82, 0.2);
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    &.error {
      border-color: #ef4444;
      box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .error-message {
    display: block;
    color: #ef4444;
    font-size: 0.875rem;
    margin-top: 0.375rem;
    font-weight: 500;
  }

  :global(.email-submit-button) {
    width: 100%;
  }

  .email-disclaimer {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
  }

  .success-message {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(15px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
    padding: 3rem;
  }

  .success-icon {
    font-size: 4rem;
    margin-bottom: 1.5rem;
    animation: bounce 1s ease-in-out;
  }

  .success-message h3 {
    color: var(--text-color1);
    font-size: 1.8rem;
    margin-bottom: 1rem;
  }

  .success-message p {
    color: var(--text-color2);
    margin-bottom: 1rem;
    line-height: 1.6;
  }

  .success-note {
    font-weight: 600;
    color: var(--text-color1) !important;
    margin-bottom: 2rem !important;
  }

  /* Animations */
  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }

  /* Tablet Responsive Design */
  @media (max-width: 1024px) and (min-width: 769px) {
    .email-form-container {
      max-width: 500px;
      margin: 0 auto;
      padding: 2.5rem;
    }
  }

  /* Mobile Responsive Design */
  @media (max-width: 768px) {
    .email-title {
      font-size: 1.8rem;
    }

    .email-form-container {
      padding: 2rem;
    }
  }

  @media (max-width: 480px) {
    .email-title {
      font-size: 1.5rem;
    }

    .email-form-container {
      padding: 1.5rem;
      margin: 0 1rem;
    }

    .success-message {
      padding: 2rem;
      margin: 0 1rem;
    }

    .success-icon {
      font-size: 3rem;
    }

    .success-message h3 {
      font-size: 1.5rem;
    }
  }

  @media (max-width: 360px) {
    .email-title {
      font-size: 1.3rem;
    }
  }

  @media (max-width: 400px) {
    /* Ensure form inputs don't overflow */
    .email-form input {
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }

    .email-form-container,
    .success-message {
      margin: 0 0.5rem;
      padding: 1rem;
    }
  }
</style>
